import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FileSpreadsheet, Upload, Download, AlertTriangle, CheckCircle2, RotateCcw, Boxes, Package, Wrench, Sparkles, ArrowLeftRight } from 'lucide-react';
import { importAPI } from '../utils/api';
import SectionHero from '../components/common/SectionHero';
import { fmt, apiError, today, downloadBlob } from '../utils/helpers';

const KIND_ICON = { raw: Boxes, product: Package, misc: Wrench };
const KIND_COLOR = { raw: '#fbbf24', product: '#38bdf8', misc: '#f472b6' };
const ATTR_LABEL = { coating: { foil: 'imp_foil', no_foil: 'imp_no_foil', mineral: 'imp_mineral' } };

export default function ImportData() {
  const { t } = useTranslation();
  const [meta, setMeta] = useState(null);
  const [wh, setWh] = useState('');
  const [file, setFile] = useState(null);
  const [pv, setPv] = useState(null);           // preview response
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [opts, setOpts] = useState({ stock_mode: 'replace', price_currency: 'USD', move_date: today() });
  const [filter, setFilter] = useState('all');
  const [done, setDone] = useState(null);
  const inp = useRef();
  const timer = useRef();

  useEffect(() => { importAPI.meta().then(r => { setMeta(r.data); setWh(r.data.warehouses[0]?.id || ''); }).catch(() => toast.error(t('error'))); }, []); // eslint-disable-line

  const read = async f => {
    if (!f) return;
    setFile(f); setBusy(true); setDone(null);
    try { const r = await importAPI.preview(f, wh); setPv(r.data); setRows(r.data.rows.map(x => ({ ...x, skip: false }))); setFilter('all'); }
    catch (e) { toast.error(apiError(e, t)); setPv(null); }
    setBusy(false);
  };
  const template = async () => { try { downloadBlob((await importAPI.template()).data, 'factory-erp-import-template.xlsx'); } catch (e) { toast.error(t('error')); } };

  // name / kind / warehouse changed -> ask the server which existing item it is now
  const rematch = (next, w = wh) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try { const r = await importAPI.rematch(next, w); setRows(cur => r.data.rows.map((x, i) => ({ ...x, skip: cur[i]?.skip, qty: cur[i]?.qty, unit: cur[i]?.unit }))); } catch (e) { /* keep */ }
    }, 450);
  };
  const setRow = (i, patch, needMatch) => setRows(cur => { const next = cur.map((r, j) => (j === i ? { ...r, ...patch } : r)); if (needMatch) rematch(next); return next; });
  const changeWh = w => { setWh(w); if (rows.length) { const next = rows.map(r => (r.warehouse ? r : { ...r, warehouse_id: w })); setRows(next); rematch(next, w); } };

  const live = rows.filter(r => !r.skip);
  const stats = useMemo(() => ({
    create: live.filter(r => r.action === 'create').length, update: live.filter(r => r.action === 'update').length,
    stock: live.filter(r => r.qty !== null && r.qty !== '' && r.qty !== undefined).length,
    warn: rows.filter(r => (r.warnings?.length || 0) + (r.mwarn?.length || 0) > 0).length,
    raw: rows.filter(r => r.kind === 'raw').length, product: rows.filter(r => r.kind === 'product').length, misc: rows.filter(r => r.kind === 'misc').length,
  }), [rows]); // eslint-disable-line
  const shown = rows.map((r, i) => [r, i]).filter(([r]) => filter === 'all' || (filter === 'warn' ? (r.warnings?.length || r.mwarn?.length) : filter === 'new' ? r.action === 'create' : r.kind === filter));

  const commit = async () => {
    if (!live.length) return toast.error(t('imp_nothing'));
    const changes = opts.stock_mode === 'replace' ? live.filter(r => r.qty !== null && r.qty !== '' && r.current_qty != null && Math.abs(Number(r.qty) - r.current_qty) > 1e-9 && r.current_qty !== 0).length : 0;
    if (changes && !window.confirm(t('imp_confirm_replace').replace('{n}', changes))) return;
    setBusy(true);
    try {
      const r = await importAPI.commit({ ...opts, filename: file?.name, rows: rows.map(x => ({ ...x, qty: x.qty === '' ? null : x.qty })) });
      setDone(r.data); setPv(null); setRows([]); toast.success(t('imp_done_title'));
    } catch (e) { toast.error(apiError(e, t)); }
    setBusy(false);
  };

  return (
    <div className="space-y-5">
      <SectionHero title={t('import_data')} subtitle={t('imp_sub')}>
        <button className="btn btn-secondary" onClick={template}><Download size={15} /> {t('imp_template')}</button>
      </SectionHero>

      {done && (
        <div className="card imp-done">
          <CheckCircle2 size={34} />
          <div><h3>{t('imp_done_title')}</h3>
            <p>{t('imp_done_line').replace('{c}', done.created).replace('{u}', done.updated).replace('{s}', done.stock_changes)}{done.skipped ? ` · ${done.skipped} ${t('imp_skipped')}` : ''}</p>
            <div className="imp-links">
              {done.by_kind.raw > 0 && <Link to="/raw-materials" className="btn btn-sm btn-secondary"><Boxes size={14} /> {t('raw_materials')} ({done.by_kind.raw})</Link>}
              {done.by_kind.product > 0 && <Link to="/finished-goods" className="btn btn-sm btn-secondary"><Package size={14} /> {t('finished_goods')} ({done.by_kind.product})</Link>}
              {done.by_kind.misc > 0 && <Link to="/misc-goods" className="btn btn-sm btn-secondary"><Wrench size={14} /> {t('misc_goods')} ({done.by_kind.misc})</Link>}
              <Link to="/inventory" className="btn btn-sm btn-ghost"><ArrowLeftRight size={14} /> {t('imp_see_kardex')}</Link>
            </div></div>
        </div>
      )}

      {!pv && (
        <div className="imp-start">
          <label className={`imp-drop ${drag ? 'on' : ''} ${busy ? 'busy' : ''}`}
            onDragOver={e => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
            onDrop={e => { e.preventDefault(); setDrag(false); read(e.dataTransfer.files?.[0]); }}>
            <input ref={inp} type="file" accept=".xlsx,.xlsm" hidden onChange={e => { read(e.target.files?.[0]); e.target.value = ''; }} />
            {busy ? <div className="spinner" /> : <FileSpreadsheet size={40} />}
            <b>{busy ? t('imp_reading') : t('imp_drop')}</b>
            <small>{t('imp_drop_hint')}</small>
          </label>
          <div className="imp-side">
            <div className="form-group"><label className="form-label">{t('imp_default_wh')}</label>
              <select className="select" value={wh} onChange={e => setWh(e.target.value)}>{(meta?.warehouses || []).map(w => <option key={w.id} value={w.id}>{w.name}</option>)}</select>
              <small className="imp-help">{t('imp_default_wh_hint')}</small></div>
            <div className="imp-formats">
              <div><Sparkles size={15} /><span><b>{t('imp_fmt_daily')}</b>{t('imp_fmt_daily_hint')}</span></div>
              <div><FileSpreadsheet size={15} /><span><b>{t('imp_fmt_template')}</b>{t('imp_fmt_template_hint')}</span></div>
            </div>
          </div>
        </div>
      )}

      {pv && <>
        <div className="card imp-head">
          <div className="imp-file"><FileSpreadsheet size={22} /><div><b>{file?.name}</b>
            <small>{pv.sheets.length} {t('imp_sheets')} · {stats.raw} {t('imp_k_raw')} · {stats.product} {t('imp_k_product')}{stats.misc ? ` · ${stats.misc} ${t('imp_k_misc')}` : ''}</small></div>
            <button className="btn btn-ghost btn-sm" onClick={() => { setPv(null); setRows([]); }}><RotateCcw size={14} /> {t('imp_other_file')}</button></div>
          <div className={`imp-stock-from ${pv.stock_sheet ? '' : 'none'}`}>
            {pv.stock_sheet ? <>{t('imp_stock_from')} <b>«{pv.stock_sheet}»</b>{pv.days_with_data.length > 1 ? ` (${t('imp_days_with_data')}: ${pv.days_with_data.length})` : ''}</>
              : t('imp_no_numbers')}
          </div>
          <div className="imp-chips">
            <span className="new">{stats.create} {t('imp_new')}</span><span className="upd">{stats.update} {t('imp_update')}</span>
            <span>{stats.stock} {t('imp_with_stock')}</span>{stats.warn > 0 && <span className="warn"><AlertTriangle size={12} /> {stats.warn} {t('imp_with_warn')}</span>}
          </div>
        </div>

        <div className="imp-opts">
          <div className="form-group" style={{ marginBottom: 0 }}><label className="form-label">{t('imp_stock_mode')}</label>
            <div className="pr-seg">{['replace', 'add', 'none'].map(m => <button key={m} type="button" className={opts.stock_mode === m ? 'on' : ''} onClick={() => setOpts({ ...opts, stock_mode: m })}><b>{t('imp_mode_' + m)}</b><small>{t('imp_mode_' + m + '_hint')}</small></button>)}</div></div>
          <div className="imp-opts-row">
            <div className="form-group"><label className="form-label">{t('imp_default_wh')}</label>
              <select className="select" value={wh} onChange={e => changeWh(e.target.value)}>{(meta?.warehouses || []).map(w => <option key={w.id} value={w.id}>{w.name}</option>)}</select></div>
            <div className="form-group"><label className="form-label">{t('imp_date')}</label><input className="input" type="date" value={opts.move_date} onChange={e => setOpts({ ...opts, move_date: e.target.value })} /></div>
            <div className="form-group"><label className="form-label">{t('imp_price_currency')}</label>
              <select className="select" value={opts.price_currency} onChange={e => setOpts({ ...opts, price_currency: e.target.value })}>{(meta?.currencies || ['USD']).map(c => <option key={c}>{c}</option>)}</select></div>
          </div>
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="mg-bar">
            <div className="mg-cats">
              {[['all', t('all'), rows.length], ['raw', t('imp_k_raw'), stats.raw], ['product', t('imp_k_product'), stats.product], ['misc', t('imp_k_misc'), stats.misc], ['new', t('imp_new'), stats.create], ['warn', t('imp_with_warn'), stats.warn]]
                .filter(([k, , n]) => k === 'all' || n > 0).map(([k, l, n]) => <button key={k} className={filter === k ? 'on' : ''} style={{ '--c': KIND_COLOR[k] || (k === 'warn' ? '#fb923c' : '#fff') }} onClick={() => setFilter(k)}>{l} ({n})</button>)}
            </div>
            <label className="pr-check" style={{ marginInlineStart: 'auto' }}><input type="checkbox" checked={live.length === rows.length} onChange={e => setRows(rows.map(r => ({ ...r, skip: !e.target.checked })))} /> {t('imp_select_all')}</label>
          </div>
          <div className="table-wrap"><table className="imp-table">
            <thead><tr><th></th><th>{t('imp_kind')}</th><th>{t('name')}</th><th>{t('unit')}</th><th>{t('imp_qty_file')}</th><th>{t('imp_qty_now')}</th><th>{t('warehouse')}</th><th>{t('imp_details')}</th><th>{t('status')}</th></tr></thead>
            <tbody>{shown.map(([r, i]) => { const I = KIND_ICON[r.kind]; const warns = [...(r.warnings || []), ...(r.mwarn || [])]; const diff = r.qty !== null && r.qty !== '' && r.current_qty != null ? Number(r.qty) - r.current_qty : null; return (
              <tr key={i} className={r.skip ? 'off' : ''}>
                <td><input type="checkbox" checked={!r.skip} onChange={e => setRow(i, { skip: !e.target.checked })} /></td>
                <td><div className="imp-kind" style={{ '--c': KIND_COLOR[r.kind] }}><I size={14} />
                  <select value={r.kind} onChange={e => setRow(i, { kind: e.target.value }, true)}>{['raw', 'product', 'misc'].map(k => <option key={k} value={k}>{t('imp_k_' + k)}</option>)}</select></div></td>
                <td><input className="input imp-name" value={r.name} onChange={e => setRow(i, { name: e.target.value }, true)} />
                  {r.existing_name && r.existing_name !== r.name ? <small className="imp-sub">{t('imp_in_system')}: {r.existing_name}</small> : null}
                  {r.similar?.length ? <small className="imp-sub warn">{t('imp_similar')}: {r.similar.join('، ')}</small> : null}</td>
                <td><input className="input imp-unit" value={r.unit || ''} onChange={e => setRow(i, { unit: e.target.value })} /></td>
                <td><input className="input imp-qty" type="number" step="any" value={r.qty ?? ''} placeholder="—" onChange={e => setRow(i, { qty: e.target.value === '' ? null : Number(e.target.value) })} /></td>
                <td className="imp-now">{r.current_qty != null ? <><bdi>{fmt(r.current_qty)}</bdi>{opts.stock_mode === 'replace' && diff !== null && Math.abs(diff) > 1e-9 ? <small className={diff > 0 ? 'pos' : 'neg'}><bdi>{diff > 0 ? '+' : '−'}{fmt(Math.abs(diff))}</bdi></small> : null}</> : '—'}</td>
                <td>{r.kind === 'misc' ? '—' : <select className="select imp-wh" value={r.warehouse_id || ''} onChange={e => setRow(i, { warehouse_id: e.target.value }, true)}>{(meta?.warehouses || []).map(w => <option key={w.id} value={w.id}>{w.name}</option>)}</select>}</td>
                <td className="imp-attrs">{Object.entries(r.attrs || {}).map(([k, v]) => <span key={k}>{ATTR_LABEL[k]?.[v] ? t(ATTR_LABEL[k][v]) : k === 'weight_kg' ? `${fmt(v)} ${t('imp_kg')}` : k === 'thickness_mm' ? `${fmt(v)} mm` : k === 'sqm_per_roll' ? `${fmt(v)} m²/${t('imp_roll')}` : k === 'label' ? `${t('imp_label')} ${v}` : String(v)}</span>)}
                  {r.price != null && <span className="price">{t('imp_price')}: <bdi>{fmt(r.price)} {opts.price_currency}</bdi></span>}</td>
                <td><span className={`badge ${r.action === 'create' ? 'badge-green' : 'badge-blue'}`}>{t(r.action === 'create' ? 'imp_new_one' : 'imp_update_one')}</span>
                  {warns.length > 0 && <span className="imp-warn" title={warns.join('\n')}><AlertTriangle size={14} /><span className="imp-warn-txt">{warns.join(' · ')}</span></span>}</td>
              </tr>); })}</tbody>
          </table></div>
        </div>

        <div className="imp-bottom">
          <p>{t('imp_bottom_hint')}</p>
          <button className="btn btn-primary btn-lg" disabled={busy || !live.length} onClick={commit}><Upload size={16} /> {t('imp_commit').replace('{n}', live.length)}</button>
        </div>
      </>}
    </div>
  );
}
